import React, { useState } from 'react'
import classNames from 'classnames'
import { Redirect } from 'react-router-dom'
import { bool, func, object } from 'prop-types'
import { useFormikContext } from 'formik'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrash } from '@fortawesome/pro-regular-svg-icons/faTrash'
import { faExclamationTriangle } from '@fortawesome/pro-regular-svg-icons/faExclamationTriangle'
import { toast } from 'react-toastify'
import { DELETE, useClient } from '@peracto/client'
import { Checkbox, Form, FormActions, Group, Input, Modal, Select, Textarea } from '@peracto/peracto-ui'

import { EMPTY_RULE, RULE_TYPE_OPTIONS, ruleTypeNeedsValue, ruleLabel } from './ruleTypes'
import { examplePreview } from './rulePreview'
import { showApiError } from './apiErrors'

const ExamplePreview = () => {
    const { values } = useFormikContext()
    const preview = examplePreview(values)

    if (!preview) {
        return null
    }

    return (
        <p
            className={classNames({
                'text-muted': !preview.checked,
                'text-success': preview.checked && preview.matches,
                'text-danger': preview.checked && !preview.matches,
            })}
            data-testid="rule-example-preview"
        >
            {preview.result
                ? `This rule turns ${values.exampleSomoraPartNumber} into ${preview.result}.`
                : `This rule doesn't apply to ${values.exampleSomoraPartNumber}.`}
            {!preview.matches && values.exampleTecDocPartNumber
                ? ` That isn't the example TecDoc part number.`
                : ''}
        </p>
    )
}

const RuleValueField = () => {
    const { values } = useFormikContext()

    if (!ruleTypeNeedsValue(values.ruleType)) {
        return null
    }

    return (
        <Input
            name="ruleValue"
            label={values.ruleType === 'strip_prefix' ? 'Prefix to remove' : 'Suffix to remove'}
            help="Exactly as it appears on Somora's part numbers, e.g. NIS or V."
            required
        />
    )
}

const RuleFields = () => (
    <>
        <Group name="Brand" id="brand">
            <Input name="brandName" label="Brand name" required testId="rule-brand-name" />
            <Input
                name="supplierAccountCode"
                label="Somora supplier account code"
                help="The MAM supplier account, keeping leading zeros, e.g. 00621. Leave blank for Somora's own brand."
                testId="rule-supplier-account-code"
            />
            <Input
                name="tecDocSupplierId"
                label="TecDoc brand number (DLNr)"
                help="TecDoc's number for the brand, e.g. 4425 for VEMA."
                required
                testId="rule-tecdoc-supplier-id"
            />
            <Checkbox name="active" label="Active" help="Switched-off rules aren't used for matching." />
        </Group>

        <Group name="Rule" id="rule">
            <Select
                name="ruleType"
                label="How Somora's part numbers become TecDoc's"
                options={RULE_TYPE_OPTIONS}
                required
                testId="rule-type"
            />
            <RuleValueField />
            <Input
                name="exampleSomoraPartNumber"
                label="Example Somora part number"
                help="Checked against the rule when you save."
            />
            <Input name="exampleTecDocPartNumber" label="Example TecDoc part number" />
            <ExamplePreview />
            <Textarea name="notes" label="Notes" />
        </Group>
    </>
)

const RuleForm = ({ data, modeEdit = false, onSubmit, schema }) => {
    const { client } = useClient()
    const [redirect, setRedirect] = useState('')
    const [showDeleteDialog, setShowDeleteDialog] = useState(false)

    const onDelete = async () => {
        try {
            await client(DELETE, 'tecdoc-brand-rules', { id: data['@id'] })
            toast.success('Brand rule deleted.')
            setRedirect('/tecdoc-brand-rules')
        } catch (e) {
            showApiError(e)
        }
    }

    if (redirect) {
        return <Redirect to={redirect} />
    }

    return (
        <>
            <FormActions>
                {modeEdit && (
                    <button
                        type="button"
                        className="btn btn--outline-primary text-danger"
                        onClick={() => setShowDeleteDialog(true)}
                    >
                        <FontAwesomeIcon icon={faTrash} className="mr-2" />
                        Delete Rule
                    </button>
                )}
            </FormActions>

            <Form values={{ ...EMPTY_RULE, ...data }} schema={schema} onSubmit={onSubmit} autoComplete="off">
                <RuleFields />
            </Form>

            {modeEdit && (
                <Modal
                    isVisible={showDeleteDialog}
                    title="Delete Brand Rule"
                    close={() => setShowDeleteDialog(false)}
                    buttons={[
                        {
                            type: 'btn-outline-secondary',
                            text: 'Close',
                            action: () => setShowDeleteDialog(false),
                        },
                        { type: 'btn-danger', text: 'Delete Rule', action: onDelete },
                    ]}
                >
                    <FontAwesomeIcon icon={faExclamationTriangle} size="4x" className="d-block mb-4" />
                    Are you sure you want to delete the rule for {ruleLabel(data)}? Its exceptions are deleted
                    too, and its parts won't match TecDoc through it any more. To stop using it for now,
                    untick Active instead.
                </Modal>
            )}
        </>
    )
}

RuleForm.propTypes = {
    data: object,
    modeEdit: bool,
    onSubmit: func.isRequired,
    schema: object.isRequired,
}

export default RuleForm
